<template>
  <div class="rule-list-view flex flex-col pb-5">
    <page-header
      :title="tr('Automations')"
      :info="tr('Unlock the Full Power of Your Social Media with Advanced Automations. 🚀 Seamlessly manage and optimize your Facebook and Instagram ads with automations for creation, scaling and stopping. 💪')"
    />

    <system-level-notifications space-top />

    <router-link
      v-if="!listIsLoading && isListWithoutFiltersEmpty"
      to="/creator-lite/new"
      class="relative isolate mt-5 block overflow-hidden rounded-xl bg-primary p-5 no-underline md:hidden"
    >
      <span
        class="absolute -inset-1/2 -z-10 animate-spin-reverse-slow motion-reduce:animate-none bg-[length:100%] bg-center bg-no-repeat"
        :style="{ backgroundImage: 'url(' + toolsGradient + ')' }"
      />
      <span
        class="-mx-5 -mt-4 mb-4 block h-50 animate-strip-scroll motion-reduce:animate-none bg-[length:auto_100%]"
        :style="{ backgroundImage: 'url(' + postStrip + ')' }"
      />
      <span class="mb-2.5 block text-xl leading-snug font-bold text-white">
        {{ tr('Generate your first AI content and boost your online presence!') }}
      </span>
      <span class="inline-flex h-9 items-center gap-2 rounded-md bg-white px-5 text-sm font-semibold text-primary shadow-md">
        <i class="icon generate-content size-4 bg-primary" />
        {{ tr('Start Infinite∞Creator Lite') }}
      </span>
    </router-link>

    <empty-screen
      v-if="!hasConnectedProfileInCompany"
      class="my-5"
      icon="play"
      :title="tr('Connect your accounts to start automating')"
      :flag-text="tr('Connect profiles')"
      :description="tr('To begin using our AI-powered automation features, you\'ll need to connect your Meta Business accounts. Link your Facebook pages, Instagram profiles, and ad accounts to unlock the full potential of automated advertising.')"
      @click="() => openOnboardingModal()"
    />

    <empty-screen
      v-if="showNewRuleCards && isListWithoutFiltersEmpty && !listIsLoading"
      class="my-5"
      icon="magic-stars"
      :title="tr('Add your first Ai advertising automation and start growing')"
      :flag-text="tr('Start creating automations')"
      :description="tr('Create AI-powered ad automation rules that will manage your post\'s promotion. Set up your first automation rule, define your goals and budget, and let our AI optimize your advertising campaigns automatically.')"
      colorful
    >
      <template #under-description>
        <ui-button as-child class="self-start">
          <router-link to="/automation/new/ai-mode">
            {{ tr('Start Ai Ad Creation') }}
          </router-link>
        </ui-button>
      </template>
    </empty-screen>

    <div class="max-md:hidden">
      <button
        v-if="showProfessionalToggle"
        type="button"
        data-slot="professional-mode-toggle"
        :aria-expanded="professionalModeIsOpen"
        class="mt-5 inline-flex cursor-pointer items-center gap-1 text-sm font-semibold text-foreground"
        @click="() => professionalModeIsOpen = !professionalModeIsOpen"
      >
        {{ tr('Professional mode') }}
        <chevron-down-icon :class="[ 'size-4 transition-transform', professionalModeIsOpen && 'rotate-180' ]" />
      </button>
      <transition-expand>
        <create-new-rule-cards
          v-if="showNewRuleCards && (!showProfessionalToggle || professionalModeIsOpen)"
          :class="showProfessionalToggle ? 'mt-1.5' : 'mt-5'"
        />
      </transition-expand>
    </div>

    <ui-button
      v-if="showNewRuleCards"
      class="mt-5 w-full md:hidden"
      @click="() => createSheetIsOpen = true"
    >
      <plus-icon />
      {{ tr('Create new automation') }}
    </ui-button>

    <help-links v-if="!listIsLoading && isListWithoutFiltersEmpty" class="mt-5" />

    <div v-if="showFilters" class="mt-5 flex flex-wrap items-center gap-3">
      <rule-action-filter :disabled="listIsLoading" class="w-full lg:w-auto" />

      <div class="relative min-w-0 flex-1 md:basis-0">
        <search-icon class="pointer-events-none absolute top-1/2 left-3 z-10 size-4 -translate-y-1/2 text-muted-foreground" />
        <ui-input
          v-model="searchTerm"
          type="search"
          class="pl-9"
          :placeholder="tr('Search') + '...'"
          :aria-label="tr('Search')"
        />
      </div>

      <ui-responsive-filters
        :active-count="activeFilterCount"
        :title="tr('Filters')"
        :clear-label="tr('Clear filters')"
        :close-label="tr('Close')"
        @clear="() => clearSelectFilters()"
      >
        <template #default="{ stacked }">
          <div :class="stacked ? 'flex flex-col gap-1.5' : 'contents'">
            <ui-label v-if="stacked">{{ tr('Brand') }}</ui-label>
            <brand-filter v-model="brandId" :class="stacked ? 'w-full' : 'min-w-0 flex-1 md:max-w-56'" />
          </div>

          <div :class="stacked ? 'flex flex-col gap-1.5' : 'contents'">
            <ui-label v-if="stacked">{{ tr('Status') }}</ui-label>
            <ui-select v-model="statusSelectValue">
              <ui-select-trigger :class="stacked ? 'w-full' : 'min-w-0 flex-1 md:max-w-40'" :aria-label="tr('Status')">
                <ui-select-value />
              </ui-select-trigger>
              <ui-select-content>
                <ui-select-item v-for="option in statusOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </ui-select-item>
              </ui-select-content>
            </ui-select>
          </div>
        </template>
      </ui-responsive-filters>
    </div>

    <empty-screen
      v-if="!listIsLoading && isListEmpty && !isEmptyFilters"
      class="my-5"
      icon="sad"
      :title="tr('No automations match your current filters')"
      :flag-text="tr('Adjust filters')"
      :description="tr('We couldn\'t find any automations that match your selected filters. Try adjusting your filter settings to see more results in your automation list.')"
    />

    <div
      v-else-if="listIsLoading || !isListEmpty"
      role="table"
      :aria-label="tr('Automations')"
      class="mt-5 flex flex-col gap-2"
    >
      <div role="row" :class="[ gridClasses, 'px-4 text-xs font-semibold whitespace-nowrap text-muted-foreground max-md:hidden' ]">
        <span role="columnheader"><span class="sr-only">{{ tr('Status') }}</span></span>
        <span role="columnheader"><span class="sr-only">{{ tr('Automation type') }}</span></span>
        <span role="columnheader" :aria-sort="ariaSort('name')">
          <button
            type="button"
            data-slot="sort-button"
            :class="sortButtonClasses"
            @click="() => sortBy('name')"
          >
            {{ tr('Name') }}
            <component :is="sortIcon('name')" class="size-3.5" />
          </button>
        </span>
        <span v-if="visibleResultsCharts" role="columnheader" class="text-right">{{ tr('Results') }}</span>
        <span role="columnheader">{{ tr('Last Run') }}</span>
        <span role="columnheader" :aria-sort="ariaSort('created_at')">
          <button
            type="button"
            data-slot="sort-button"
            :class="sortButtonClasses"
            @click="() => sortBy('created_at')"
          >
            {{ tr('Date created') }}
            <component :is="sortIcon('created_at')" class="size-3.5" />
          </button>
        </span>
        <span role="columnheader" class="text-center">{{ tr('Task Done') }}</span>
        <span role="columnheader" />
      </div>

      <div
        v-for="row in list"
        :key="row.id"
        role="row"
        :data-ai-rule="row.group_id !== null || null"
        :class="[ gridClasses, rowClasses, GLOW_TRANSITION, row.group_id === null && actionStyle(row).glow ]"
      >
        <div role="cell" class="max-md:order-last max-md:col-span-2 max-md:border-t max-md:border-border max-md:pt-3">
          <switcher
            v-model="row.active"
            v-tooltip="row.isBlockedByInactiveBrand && !row.active ? tr('The brand of this automation is inactive. Reactivate the brand to switch the automation on.') : null"
            :tone="actionStyle(row).tone"
            :disabled="isRuleSwitchDisabled(row)"
            :aria-label="tr('Active')"
            @update:model-value="(val) => onChangeRuleStatus(row, val)"
          />
        </div>

        <div role="cell" class="max-md:absolute max-md:top-4 max-md:right-4">
          <rule-type-badge
            v-tooltip="actionLabel(row)"
            compact
            :action="row.action"
            :ai="row.group_id !== null"
          />
        </div>

        <div role="cell" class="flex min-w-0 flex-col gap-0.5 max-md:col-span-2 max-md:pr-10">
          <access-handled-router-link
            :to="'/automation/' + row.id + '/overview'"
            class="line-clamp-2 text-sm font-medium text-foreground no-underline hover:text-primary"
          >
            {{ row.name }}
          </access-handled-router-link>

          <div class="flex flex-wrap gap-1.5">
            <ui-badge v-if="row.brand" variant="secondary" class="max-w-full truncate px-1.5 py-0 text-[11px] font-normal">
              {{ row.brand.name }}
            </ui-badge>
            <rule-error-flags :rule="row" />
          </div>
        </div>

        <div v-if="visibleResultsCharts" role="cell" :class="[ 'md:justify-self-end', !hasChart(row) && 'max-md:hidden' ]">
          <template v-if="hasChart(row)">
            <span :class="cellLabelClasses">
              {{ tr('Results') }}
              <span class="font-normal text-muted-foreground">({{ tr('Last 30 days') }})</span>
            </span>
            <analytics-chart
              :isLoading="chartIsLoading(row)"
              :width="resultsChartSizes.width"
              :height="resultsChartSizes.height"
              :data="getChartData(row)"
              :total="getTotalMetricValue(row)"
              :stroke-width="2"
              :label="getActionLabel(row)"
              class="max-md:mx-auto"
            />
          </template>
        </div>

        <div role="cell" class="flex items-center justify-between gap-2 text-sm max-md:col-span-2 md:justify-start">
          <span :class="cellLabelClasses">{{ tr('Last Run') }}</span>

          <span v-if="row.is_running" class="flex items-center gap-1.5 text-xs text-muted-foreground">
            <loader-circle-icon class="size-4 animate-spin" />
            {{ tr('Running now...') }}
          </span>
          <span v-else class="flex items-center gap-1.5">
            <rule-next-run-tooltip v-if="row.active" :entity="row" class="flex">
              <clock-icon class="size-4 text-muted-foreground" />
            </rule-next-run-tooltip>
            <span v-if="row.last_run" v-tooltip="formatToYMD(row.last_run) + '. ' + formatTime(row.last_run)">
              {{ timeAgo(row.last_run, tr) }}
            </span>
            <template v-else>–</template>
          </span>
        </div>

        <div role="cell" class="flex items-center justify-between gap-2 text-sm max-md:col-span-2">
          <span :class="cellLabelClasses">{{ tr('Date created') }}</span>
          <span v-if="row.created_at" v-tooltip="formatToYMD(row.created_at) + '. ' + formatTime(row.created_at)">
            {{ timeAgo(row.created_at, tr) }}
          </span>
          <template v-else>–</template>
        </div>

        <div role="cell" class="flex items-center justify-between gap-2 text-sm tabular-nums max-md:col-span-2 md:justify-center max-md:pr-10">
          <span :class="cellLabelClasses">{{ tr('Task Done') }}</span>
          {{ row.triggered }}
        </div>

        <div role="cell" class="max-md:absolute max-md:right-4 max-md:bottom-3.5">
          <arrow-link :to="'/automation/' + row.id + '/overview'" :label="row.name" />
        </div>
      </div>

      <template v-if="listIsLoading || hasMore">
        <div
          v-for="action in (isListEmpty ? [ 'start', 'scaling_up', 'stop' ] : [ 'start', 'scaling_up' ])"
          :key="'automations-loader-skeleton--' + action"
          ref="scrollPagerTrigger"
          role="row"
          :class="[ gridClasses, rowClasses ]"
        >
          <!-- The cells of a loaded row, with placeholders of the same size. -->
          <div role="cell" class="max-md:order-last max-md:col-span-2 max-md:border-t max-md:border-border max-md:pt-3">
            <!-- Built like the switch (track + thumb), so it sits on the same baseline. -->
            <span class="relative inline-flex items-center">
              <span data-slot="skeleton" class="inline-flex h-[1.15rem] w-8 animate-pulse items-center rounded-full border border-transparent bg-primary/10"><span class="block size-4" /></span>
            </span>
          </div>
          <div role="cell" class="max-md:absolute max-md:top-4 max-md:right-4">
            <span class="relative inline-flex size-6 items-center justify-center"><i class="size-4" /><ui-skeleton class="absolute inset-0 rounded-md" /></span>
          </div>
          <div role="cell" class="flex min-w-0 flex-col gap-0.5 max-md:col-span-2 max-md:pr-10">
            <span class="flex flex-col">
              <span class="flex h-5 items-center"><ui-skeleton class="h-3.5 w-full max-w-100" /></span>
              <span class="flex h-5 items-center"><ui-skeleton class="h-3.5 w-2/3" /></span>
            </span>
            <span class="flex h-5 items-center"><ui-skeleton class="h-4 w-18 rounded-md" /></span>
          </div>
          <div v-if="visibleResultsCharts" role="cell" class="max-md:hidden" />
          <div role="cell" class="flex h-5 items-center justify-between gap-2 text-sm max-md:col-span-2 md:justify-start">
            <span :class="cellLabelClasses">{{ tr('Last Run') }}</span>
            <ui-skeleton class="h-3.5 w-20" />
          </div>
          <div role="cell" class="flex h-5 items-center justify-between gap-2 text-sm max-md:col-span-2">
            <span :class="cellLabelClasses">{{ tr('Date created') }}</span>
            <ui-skeleton class="h-3.5 w-20" />
          </div>
          <div role="cell" class="flex h-5 items-center justify-between gap-2 text-sm max-md:col-span-2 md:justify-center max-md:pr-10">
            <span :class="cellLabelClasses">{{ tr('Task Done') }}</span>
            <ui-skeleton class="h-3.5 w-6" />
          </div>
          <div role="cell" class="max-md:absolute max-md:right-4 max-md:bottom-3.5">
            <ui-skeleton class="size-8 rounded-full" />
          </div>
        </div>
      </template>
    </div>

    <ui-sheet v-if="showNewRuleCards" :open="createSheetIsOpen" @update:open="(value) => createSheetIsOpen = value">
      <ui-sheet-content side="bottom" class="mx-auto max-w-sm gap-3 p-4">
        <ui-sheet-header class="p-0">
          <ui-sheet-title>{{ tr('Create new automation') }}</ui-sheet-title>
          <ui-sheet-description class="sr-only">
            {{ tr('Choose the type of the new automation.') }}
          </ui-sheet-description>
        </ui-sheet-header>
        <create-new-rule-cards stacked @select="() => createSheetIsOpen = false" />
      </ui-sheet-content>
    </ui-sheet>
  </div>
</template>

<script>
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  Clock,
  LoaderCircle,
  Plus,
  Search,
} from '@lucide/vue';

import postStrip from '@/assets/img/social-media-post-strip.png';
import toolsGradient from '@/assets/img/tools-gradient.png';
import { CAMPAIGN_STATUS, RULE_ACTION } from '@/consts.js';
import DashboardMixin from '@/mixins/Dashboard.js';
import RulePollingMixin from '@/mixins/RulePolling.js';
import RuleSwitchMixin from '@/mixins/RuleSwitch.js';
import ScrollPagerMixin from '@/mixins/ScrollPager.js';
import AnalyticsChart from '@/ui/components/AnalyticsChart.vue';
import ArrowLink from '@/ui/components/ArrowLink.vue';
import BrandFilter from '@/ui/components/BrandFilter.vue';
import CreateNewRuleCards from '@/ui/components/CreateNewRuleCards.vue';
import EmptyScreen from '@/ui/components/EmptyScreen.vue';
import HelpLinks from '@/ui/components/HelpLinks.vue';
import PageHeader from '@/ui/components/PageHeader.vue';
import RuleActionFilter from '@/ui/components/RuleActionFilter.vue';
import RuleErrorFlags from '@/ui/components/RuleErrorFlags.vue';
import RuleNextRunTooltip from '@/ui/components/RuleNextRunTooltip.vue';
import RuleTypeBadge from '@/ui/components/RuleTypeBadge.vue';
import Switcher from '@/ui/components/Switcher.vue';
import SystemLevelNotifications from '@/ui/components/SystemLevelNotifications.vue';
import TransitionExpand from '@/ui/components/TransitionExpand.vue';
import { GLOW_TRANSITION, ruleActionStyle } from '@/ui/utils/ruleAction.js';
import { formatTime, formatToYMD, timeAgo } from '@/utils/date';
import debounce from '@/utils/debounce.js';

const STATUS_ALL = 'all';

export default {
  name: 'RuleListView',
  components: {
    AnalyticsChart,
    ArrowLink,
    BrandFilter,
    ChevronDownIcon: ChevronDown,
    ClockIcon: Clock,
    CreateNewRuleCards,
    EmptyScreen,
    HelpLinks,
    LoaderCircleIcon: LoaderCircle,
    PlusIcon: Plus,
    RuleActionFilter,
    RuleErrorFlags,
    RuleNextRunTooltip,
    RuleTypeBadge,
    SearchIcon: Search,
    PageHeader,
    Switcher,
    SystemLevelNotifications,
    TransitionExpand,
  },
  mixins: [ DashboardMixin, RulePollingMixin, RuleSwitchMixin, ScrollPagerMixin ],
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.temporaryDisableWatchers();

      if (!from || !from.name || vm.isListEmpty || from.path.split('/')[1] !== to.path.split('/')[1]) {
        let keepFilters = false;
        if (from && from.name === 'Dashboard' && vm.brandId) {
          keepFilters = true;
        }

        vm.$store.dispatch('rules/resetView', keepFilters);
        vm.loadNewData();
      }
      else {
        for (const rule of vm.list) {
          vm.observeRule(rule);
        }
      }
    });
  },
  data() {
    return {
      disableWatchers: false,
      debouncedLoadData: debounce(() => this.loadData(), 500),
      professionalModeIsOpen: false,
      GLOW_TRANSITION,
      createSheetIsOpen: false,
      postStrip,
      toolsGradient,
      sortButtonClasses: 'inline-flex cursor-pointer items-center gap-1 hover:text-foreground',
      cellLabelClasses: 'text-sm font-semibold text-foreground md:hidden',
    };
  },
  computed: {
    visibleResultsCharts() {
      return this.$root.screenWidth >= 1024 || this.$root.screenWidth < 768;
    },
    resultsChartSizes() {
      // Desktop size.
      if (this.$root.screenWidth >= 1024) {
        return { width: 110, height: 40 };
      }

      // Mobile size.
      return { width: 250, height: 60 };
    },
    // One grid for the header and the rows: a stacked card on mobile, columns
    // from md (the results chart column only from lg).
    gridClasses() {
      return [
        'grid items-center gap-x-4',
        'max-md:relative max-md:grid-cols-[auto_1fr] max-md:gap-y-3',
        this.visibleResultsCharts
          ? 'md:grid-cols-[2.75rem_1.5rem_minmax(0,1fr)_7.5rem_9rem_8rem_4.5rem_2rem]'
          : 'md:grid-cols-[2.75rem_1.5rem_minmax(0,1fr)_9rem_8rem_4.5rem_2rem]',
      ];
    },
    rowClasses() {
      return 'glass-tile rounded-xl p-4 md:min-h-13 md:py-1.5';
    },
    isListEmpty() {
      return this.list.length == 0;
    },
    isListWithoutFiltersEmpty() {
      return !this.$store.getters['rules/hasAnyRules'];
    },
    showProfessionalToggle() {
      return !this.listIsLoading && this.isListWithoutFiltersEmpty && this.hasConnectedProfileInCompany;
    },
    showNewRuleCards() {
      return !this.$root.loggedInUser.isRestrictedViewer && !this.$root.isSupportMode && this.hasConnectedProfileInCompany;
    },
    showFilters() {
      if (!this.hasConnectedProfileInCompany) {
        return false;
      }

      if (this.listIsLoading) {
        return true;
      }

      if (this.isListWithoutFiltersEmpty) {
        return false;
      }

      return !this.isListEmpty || !this.isEmptyFilters;
    },
    // The filters in the phone filter sheet (the type and the search stay in view).
    activeFilterCount() {
      return [ this.brandId !== null, this.statusFilter !== null ].filter(Boolean).length;
    },
    isEmptyFilters() {
      return this.searchTerm.trim().length === 0
        && this.brandId === null
        && this.statusFilter === null
        && this.$store.getters['rules/actionFilterIsEmpty'];
    },
    hasConnectedProfileInCompany() {
      if (!this.$root.currentCompany) {
        return true;
      }

      const profileItems = this.$root.currentCompany.connected_profiles.filter(profile => profile.platform === 'facebook');
      return profileItems.length !== 0;
    },
    statusOptions() {
      return [
        { value: STATUS_ALL, label: this.tr('All statuses') },
        { value: '1', label: this.tr('Active') },
        { value: '0', label: this.tr('Inactive') },
      ];
    },
    // The select works with strings; the store keeps null / 1 / 0.
    statusSelectValue: {
      get() {
        return this.statusFilter === null ? STATUS_ALL : String(this.statusFilter);
      },
      set(value) {
        this.statusFilter = value === STATUS_ALL ? null : Number(value);
      },
    },
    listIsLoading() {
      return this.$store.getters['rules/isLoading'];
    },
    hasMore() {
      return this.$store.getters['rules/hasMore'];
    },
    list() {
      return this.$store.getters['rules/list'];
    },
    searchTerm: {
      get() {
        return this.$store.getters['rules/searchTerm'];
      },
      set(value) {
        this.$store.commit('rules/setSearchTerm', value);
      },
    },
    actionFilter() {
      return this.$store.getters['rules/actionFilter'];
    },
    brandId: {
      get() {
        return this.$store.getters['rules/brandId'];
      },
      set(value) {
        this.$store.commit('rules/setBrandId', value);
      },
    },
    statusFilter: {
      get() {
        return this.$store.getters['rules/statusFilter'];
      },
      set(value) {
        this.$store.commit('rules/setStatusFilter', value);
      },
    },
    order() {
      return this.$store.getters['rules/order'];
    },
    orderBy() {
      return this.$store.getters['rules/orderBy'];
    },
  },
  watch: {
    searchTerm(val) {
      if (this.disableWatchers) {
        return;
      }

      if (!val || val.trim().length === 0) {
        this.loadData();
      }
      else {
        this.debouncedLoadData();
      }
    },
    actionFilter() {
      if (this.disableWatchers) {
        return;
      }

      this.loadData();
    },
    brandId() {
      if (this.disableWatchers) {
        return;
      }

      this.loadData();
    },
    statusFilter() {
      if (this.disableWatchers) {
        return;
      }

      this.loadData();
    },
  },
  methods: {
    clearSelectFilters() {
      this.brandId = null;
      this.statusFilter = null;
    },
    formatToYMD,
    formatTime,
    timeAgo,
    temporaryDisableWatchers() {
      this.disableWatchers = true;
      setTimeout(() => this.disableWatchers = false, 100);
    },
    loadData() {
      if (!this.$root.isLoggedIn) {
        return;
      }

      this.unobserveAllRules();
      this.$store.dispatch('rules/resetView', true);
      this.loadNewData();
    },
    loadNewData() {
      if (this.listIsLoading) {
        return;
      }

      const loadMore = this.$store.dispatch('rules/loadMoreData');
      if (loadMore) {
        loadMore
          .catch(err => this.error(err))
          .finally(() => {
            for (const rule of this.list) {
              this.observeRule(rule);
            }

            if (this.hasMore) {
              this.$nextTick(() => this.initScrollPager('scrollPagerTrigger', 'loadNewData'));
            }
          });
      }
    },
    // Same column again flips the direction, a new column starts ascending.
    sortBy(key) {
      const direction = this.orderBy === key && this.order === 'asc' ? 'desc' : 'asc';

      this.$store.commit('rules/setOrderBy', key);
      this.$store.commit('rules/setOrder', direction);
      this.loadData();
    },
    sortIcon(key) {
      if (this.orderBy !== key) {
        return ArrowUpDown;
      }

      return this.order === 'asc' ? ArrowUp : ArrowDown;
    },
    ariaSort(key) {
      if (this.orderBy !== key) {
        return 'none';
      }

      return this.order === 'asc' ? 'ascending' : 'descending';
    },
    isRuleSwitchDisabled(rule) {
      return this.$root.loggedInUser.isRestrictedViewer
        || this.$root.isSupportMode
        || rule.campaign_status === CAMPAIGN_STATUS.DELETED
        || rule.hasLostAccessAssets
        || (rule.isBlockedByInactiveBrand && !rule.active);
    },
    actionStyle(rule) {
      return ruleActionStyle(rule.action);
    },
    actionLabel(rule) {
      return this.tr('automation_type_' + rule.action);
    },
    hasChart(row) {
      return row.action === RULE_ACTION.START || row.action === RULE_ACTION.SCALING_UP;
    },
    getAnalyticsItem(row) {
      return this.$store.getters['rules/ruleAnalytics'](row.id);
    },
    chartIsLoading(row) {
      const analytic = this.getAnalyticsItem(row);
      return analytic === null;
    },
    getChartData(row) {
      const analytic = this.getAnalyticsItem(row);
      if (analytic === null) {
        return [];
      }

      return analytic.data.map(dailyMetric => ({
        x: new Date(dailyMetric.date),
        y: dailyMetric.value,
      }));
    },
    getTotalMetricValue(row) {
      const analytic = this.getAnalyticsItem(row);
      if (analytic === null) {
        return 0;
      }

      return analytic.total;
    },
    getActionLabel(row) {
      const analytic = this.getAnalyticsItem(row);

      return analytic ? this.tr(analytic.main_metric) : null;
    },
  },
};
</script>
